import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeliveryAttempts from './DeliveryAttempts';
import MessageMedia from './MessageMedia';
import { apiUrl } from "../../config/api";

function headers() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString([], {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  }) : "";
}

function MetaMessages({ channel }) {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loadedDetail, setDetail] = useState(null);
  const [detailKey, setDetailKey] = useState("");
  const selectionRef = useRef("");
  const detailRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const sendingRef = useRef(false);
  const replyRevisionRef = useRef(0);
  const mountedRef = useRef(true);
  const channelRef = useRef(channel); channelRef.current = channel;
  const attemptsRef = useRef(new Map());
  selectionRef.current = `${channel}:${selectedId}`;
  const detail = detailKey === selectionRef.current ? loadedDetail : null;
  const [reply, setReplyState] = useState("");
  const setReply = useCallback((value) => { replyRevisionRef.current += 1; setReplyState(value); }, []);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const selected = useMemo(
    () => conversations.find((item) => String(item.id) === String(selectedId)),
    [conversations, selectedId]
  );

  const load = useCallback(async () => {
    const requestId = ++listRequestRef.current;
    setError("");
    try {
      const response = await fetch(apiUrl(`/oliva/meta-messages/conversations?channel=${channel}`), {
        headers: headers(),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "No se pudieron cargar las conversaciones.");
      if (!mountedRef.current || channel !== channelRef.current || requestId !== listRequestRef.current) return;
      setConversations(data.conversations || []);
      setSelectedId((current) => current || data.conversations?.[0]?.id || "");
    } catch (requestError) {
      if (mountedRef.current && requestId === listRequestRef.current) setError(requestError.message);
    }
  }, [channel]);

  const loadDetail = useCallback(async (id) => {
    const key = `${channel}:${id}`;
    const requestId = ++detailRequestRef.current;
    if (!id) return setDetail(null);
    try {
      const response = await fetch(apiUrl(`/oliva/meta-messages/conversations/${id}`), {
        headers: headers(),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "No se pudo cargar la conversación.");
      if (!mountedRef.current || key !== selectionRef.current || requestId !== detailRequestRef.current) return;
      setDetailKey(key);
      setDetail(data);
    } catch (requestError) {
      if (key === selectionRef.current && requestId === detailRequestRef.current) setError(requestError.message);
    }
  }, [channel]);

  useEffect(() => {
    setSelectedId("");
    setDetail(null);
    load();
  }, [channel, load]);

  useEffect(() => {
    setDetail(null);
    setDetailKey("");
    setReply("");
    loadDetail(selectedId);
    return () => { detailRequestRef.current += 1; };
  }, [selectedId, channel, loadDetail, setReply]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; listRequestRef.current += 1; detailRequestRef.current += 1; };
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([load(), selectedId ? loadDetail(selectedId) : Promise.resolve()]);
  }, [load, loadDetail, selectedId]);
  useEffect(() => {
    const updateVisible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const timer = window.setInterval(updateVisible, 5000);
    window.addEventListener('online', updateVisible); document.addEventListener('visibilitychange', updateVisible);
    return () => { window.clearInterval(timer); window.removeEventListener('online', updateVisible); document.removeEventListener('visibilitychange', updateVisible); };
  }, [refresh]);

  async function update(payload) {
    const response = await fetch(apiUrl(`/oliva/meta-messages/conversations/${selectedId}`), {
      method: "PATCH", headers: headers(), body: JSON.stringify({ ...payload, lastMessageId: Math.max(0, ...(detail?.messages || []).map(message => Number(message.id))) }),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || "No se pudo actualizar.");
    await load();
    await loadDetail(selectedId);
  }

  async function sendReply() {
    if (!reply.trim() || !detail || sendingRef.current) return;
    const targetId = selectedId;
    const targetKey = selectionRef.current;
    const sentRevision = replyRevisionRef.current;
    let username = ''; try { username = JSON.parse(localStorage.getItem('sessionData') || '{}').username || ''; } catch { /* no persistent session */ }
    const storageKey = `meta-reply:${username}:${targetId}`;
    let attempt = attemptsRef.current.get(storageKey); try { attempt = JSON.parse(localStorage.getItem(storageKey) || 'null') || attempt; } catch { /* in-memory request still has its key */ }
    if (attempt && attempt.bodyText !== reply.trim()) {
      const status = detail.attempts?.find(item => item.operation_key === attempt.key)?.status;
      if (!['sent','failed'].includes(status)) { setError('Verifica la respuesta anterior antes de cambiar el texto y enviar otra.'); return; }
      attempt = null;
    }
    if (!attempt) attempt = { key: `meta-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`, bodyText: reply.trim() };
    attemptsRef.current.set(storageKey, attempt);
    try { localStorage.setItem(storageKey, JSON.stringify(attempt)); } catch { /* Server-side conversation claim also prevents an overlapping send. */ }
    sendingRef.current = true;
    setSending(true);
    setError("");
    try {
      const response = await fetch(apiUrl(`/oliva/meta-messages/conversations/${targetId}/reply`), {
        method: "POST", headers: headers(), body: JSON.stringify({ bodyText: attempt.bodyText, operationKey: attempt.key }),
      });
      const data = await response.json();
      if (!data.success) {
        if (data.retrySafe) { attemptsRef.current.delete(storageKey); try { localStorage.removeItem(storageKey); } catch { /* optional storage */ } }
        await loadDetail(targetId);
        throw new Error(data.message || 'Meta rechazó la respuesta.');
      }
      attemptsRef.current.delete(storageKey);
      try { localStorage.removeItem(storageKey); } catch { /* optional storage */ }
      if (targetKey !== selectionRef.current) return;
      if (sentRevision === replyRevisionRef.current) setReply("");
      await load();
      await loadDetail(selectedId);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  const channelLabel = channel === "instagram" ? "Instagram" : "WhatsApp";
  return (
    <div className="site-messages">
      <section className="site-messages__list" aria-label={`Mensajes de ${channelLabel}`}>
        <div className="site-messages__list-top">
          <div><span className="site-messages__badge">{channelLabel}</span><h1>Conversaciones</h1></div>
          <button type="button" onClick={refresh} title="Actualizar"><span className="material-symbols-outlined">refresh</span></button>
        </div>
        {error ? <div className="site-messages__error">{error}</div> : null}
        {!conversations.length ? <p className="site-messages__empty">No hay conversaciones de {channelLabel}.</p> : null}
        <div className="site-messages__items">
          {conversations.map((conversation) => (
            <button
              type="button"
              key={conversation.id}
              className={`site-messages__item ${String(selectedId) === String(conversation.id) ? "is-active" : ""}`}
              onClick={() => setSelectedId(conversation.id)}
            >
              <span className={`site-messages__dot ${conversation.is_unread ? "is-unread" : ""}`} />
              <span className="site-messages__item-main">
                <strong>{conversation.contact_name || conversation.contact_phone || conversation.contact_provider_id}</strong>
                <small>{conversation.last_message_preview || "Nueva conversación"}</small>
              </span>
              <span className="site-messages__item-meta">
                {conversation.unread_count ? <span>{conversation.unread_count}</span> : null}
                <small>{formatDate(conversation.last_message_at)}</small>
              </span>
            </button>
          ))}
        </div>
      </section>
      <section className="site-messages__detail" aria-label="Conversación">
        {!selected || !detail ? <div className="site-messages__placeholder">Selecciona una conversación.</div> : <>
          <div className="site-messages__contact">
            <div>
              <span className="site-messages__badge">{channelLabel}</span>
              <h2>{selected.contact_name || selected.contact_phone || selected.contact_provider_id}</h2>
              <p>{selected.contact_phone || `ID ${selected.contact_provider_id}`}</p>
            </div>
            <div className="site-messages__actions">
              <button type="button" onClick={() => update({ isUnread: false })}><span className="material-symbols-outlined">drafts</span>Leído</button>
              <button type="button" onClick={() => update({ status: "closed" })}><span className="material-symbols-outlined">archive</span>Cerrar</button>
            </div>
          </div>
          <div className="site-messages__thread">
            {(detail.messages || []).map((message) => <article
              key={message.id}
              className={`site-messages__bubble site-messages__bubble--${message.direction}`}
            >
              <div><strong>{message.sender_name || (message.direction === "outbound" ? "OVM" : channelLabel)}</strong><small>{formatDate(message.created_at)}</small></div>
              {message.is_historical && <small>Historial importado</small>}
              <p>{message.body_text}</p>
              {(message.attachments || []).map((attachment, index) => <MessageMedia key={`${message.id}:${index}`} attachment={attachment} headers={headers} />)}
              {message.message_type !== 'text' && !(message.attachments || []).length && !message.body_text?.replace(/^\[.*\]$/, '') && <p>Contenido no compatible. Revisa el mensaje en el proveedor.</p>}
            </article>)}
          </div>
          <a href={channel === 'instagram' ? 'https://www.instagram.com/direct/inbox/' : 'https://web.whatsapp.com/'} target="_blank" rel="noreferrer">Abrir bandeja en {channelLabel}</a>
          <DeliveryAttempts key={`${channel}:${selectedId}`} attempts={detail.attempts} endpoint={`/oliva/meta-messages/conversations/${selectedId}/reply-attempts`} headers={headers} onResolved={refresh} />
          <div className="site-messages__composer">
            <textarea value={reply} onChange={(event) => setReply(event.target.value)} placeholder={`Responder por ${channelLabel}...`} />
            <button type="button" onClick={sendReply} disabled={sending || !detail || !reply.trim()}><span className="material-symbols-outlined">send</span>{sending ? "Enviando" : "Responder"}</button>
          </div>
        </>}
      </section>
    </div>
  );
}

export default MetaMessages;
